import { cn } from '@/lib/utils';
import { PropsWithChildren, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import type { IStep } from '@chainlit/react-client';

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@/components/ui/accordion';

import { debugModeState } from 'state/chat';

import { useElapsedSeconds } from './useElapsedSeconds';
import { useIdleFillMessage } from './useIdleFillMessage';

interface Props {
    step: IStep;
    isRunning?: boolean;
}

export default function Step({
    step,
    children,
    isRunning
}: PropsWithChildren<Props>) {
    const using = useMemo(() => {
        return isRunning && step.start && !step.end && !step.isError;
    }, [step, isRunning]);

    const hasContent = step.input || step.output || step.steps?.length;
    const isError = step.isError;
    const stepName = step.name;
    const stepNameUnderScoreReplacedWithSpace = stepName;

    const isDebuggingEnabled = useRecoilValue(debugModeState);
    const alwaysShow = step.metadata?.alwaysShow === true;
    const showAccordion = isDebuggingEnabled || alwaysShow;

    const displayedStepName = useIdleFillMessage(
        stepNameUnderScoreReplacedWithSpace,
        !!using
    );

    const elapsedSeconds = useElapsedSeconds(step.start, !!using);

    const renderStepName = (name: string) => {
        const cleaned = name
            .replace(/\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu, '')
            .trim();
        return (
            <>
                <span className="block min-w-0 text-left truncate">
                    {cleaned}
                </span>
                {elapsedSeconds ? (
                    <span className="tabular-nums shrink-0">
                        {elapsedSeconds}s
                    </span>
                ) : null}
            </>
        );
    };

    // If there's no content, just render the status without accordion
    if (!hasContent) {
        return (
            <div className="flex flex-col grow w-0">
                <p
                    className={cn(
                        'flex items-center w-full gap-2 font-normal text-left',
                        isError && 'text-red-500',
                        !using && 'text-muted-foreground',
                        using && 'loading-shimmer'
                    )}
                    id={`step-${stepName}`}
                >
                    {renderStepName(displayedStepName)}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col grow w-0 min-w-0">
            <Accordion
                type="single"
                collapsible
                defaultValue={step.defaultOpen ? step.id : undefined}
                className="w-full min-w-0"
            >
                <AccordionItem value={step.id} className="border-none min-w-0">
                    <AccordionTrigger
                        className={cn(
                            'flex justify-start items-center gap-2 p-0 min-w-0 font-normal text-left hover:no-underline transition-none',
                            isError && 'text-red-500',
                            !using &&
                                'text-muted-foreground hover:text-foreground',
                            using && 'loading-shimmer',
                            !showAccordion && '[&_svg]:hidden' // Hide the accordion icon if not showing accordion
                        )}
                        id={`step-${stepName}`}
                    >
                        {renderStepName(displayedStepName)}
                    </AccordionTrigger>
                    {showAccordion && (
                        <AccordionContent>
                            <div className="grow ml-1 border-foreground">
                                {children}
                            </div>
                        </AccordionContent>
                    )}
                </AccordionItem>
            </Accordion>
        </div>
    );
}
